import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableContainer,
  Typography,
  Box,
} from "@mui/material";

// Styles & Mock Data
import {
  courseFields,
  getModifiedCourseLists,
  getScores,
  getTrack,
  holesList,
  indexList,
  parList,
  playerFields,
  vsRow,
} from "./mockData";
import {
  StyledScoreBox,
  StyledTableCell,
  StyledTableRow,
  useStyles,
} from "./styles";

// Local Components
import ArrowStatus from "./Components/ArrowStatus";
import OpponentName from "./Components/OpponentName";
import CustomBadge from "./Components/Badge";

// Actions
import {
  clearScoreCard,
  getScore,
} from "../../redux/actions/smp-action/get-score-actions";

// component
const SideBoxItem = ({ value }) => {
  return (
    <Typography variant="body2" my={1} color="initial" fontWeight="bold">
      {value}
    </Typography>
  );
};

const ScoreboardTable = ({ handicapDifferential }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { id: match_id } = useParams();
  const playerHandicap = +handicapDifferential?.match(/(\d+)/)[0];

  // Local States
  const [courseInfo, setcourseInfo] = useState(courseFields);
  const [playerInfo, setPlayerInfo] = useState(playerFields);
  const [score, setScore] = useState(null);
  const [middleRow, setMiddleRow] = useState(vsRow);
  const [wonPlayoffInfo, setWonPlayoffInfo] = useState(null);
  const [parIndexList, setParIndexList] = useState({
    holesList,
    indexList,
    parList,
  });

  // Selectors
  const getScoreSelector = useSelector((state) => state.getScoreReducer);
  const groupsAndTeesSelector = useSelector(
    (state) => state.groupsAndTeesReducer
  );

  // UseEffects
  useEffect(() => {
    dispatch(clearScoreCard());
    setWonPlayoffInfo(null);

    if (match_id != null && state != null) {
      const postPayload = { round_id: state._id, match_id };
      dispatch(getScore(postPayload));
    }
    setScore(null);

    return () => dispatch(clearScoreCard());
  }, [dispatch, match_id, state]);

  useEffect(() => {
    if (
      !getScoreSelector.loading &&
      getScoreSelector.data &&
      !getScoreSelector.data.error
    ) {
      if (getScoreSelector.data.roundMatchData != undefined) {
        const groupMatchData = getScoreSelector.data?.roundMatchData[0];
        const matches = groupMatchData?.matches;
        const groupData1 = matches?.group1[0];
        const groupData2 = matches?.group2[0];

        if (
          matches?.score_up_group != null &&
          matches?.score_up_value != null &&
          matches?.score_up_value == "SD"
        ) {
          setWonPlayoffInfo({
            scoreUpGroup: matches?.score_up_group,
            scoreUpValue: matches?.score_up_value,
          });
        }

        const courseInfoData = {
          name: groupMatchData?.course_name,
          date: matches?.date,
          time: matches?.time,
        };

        const playerInfoData = {
          group1: {
            name: groupData1?.name,
            teeColor: groupData1?.tee,
            handicapValue: groupData1?.playing_handicap,
          },
          group2: {
            name: groupData2?.name,
            teeColor: groupData2?.tee,
            handicapValue: groupData2?.playing_handicap,
          },
        };

        if (courseInfoData.date != null) setcourseInfo(courseInfoData);
        if (playerInfoData.group1.teeColor != null)
          setPlayerInfo(playerInfoData);

        const getModifiedList = getModifiedCourseLists(groupData1?.mp_score);

        if (getModifiedList.indexList.length > 0) {
          setParIndexList({
            holesList: getModifiedList.holesList,
            indexList: getModifiedList.indexList,
            parList: getModifiedList.parList,
          });
        }

        if (groupData1?.mp_score.length > 0) {
          const statusTrackLength = matches.match_status_track?.length;

          const scoreList1 = getScores(groupData1?.mp_score);
          const scoreList2 = getScores(groupData2?.mp_score);

          setScore({ group1: scoreList1, group2: scoreList2 });
        }

        if (matches?.match_status_track != undefined) {
          const statusTrack = matches?.match_status_track;
          const modifiedStatusTrack = getTrack(statusTrack);
          setMiddleRow(modifiedStatusTrack);
        }
      }
    }
  }, [getScoreSelector, playerHandicap]);

  useEffect(() => {
    if (
      !groupsAndTeesSelector.loading &&
      groupsAndTeesSelector.status != undefined
    ) {
      const tee1 = groupsAndTeesSelector?.status?.groupsData[0].tees_1.filter(
        (el) => el.is_default
      );
      const tee2 = groupsAndTeesSelector?.status?.groupsData[0].tees_2.filter(
        (el) => el.is_default
      );

      setPlayerInfo((prev) => ({
        group1: {
          ...prev.group1,
          teeColor: tee1[0].tee_color,
          handicapValue: tee1[0].handicap_value,
        },
        group2: {
          ...prev.group2,
          teeColor: tee2[0].tee_color,
          handicapValue: tee2[0].handicap_value,
        },
      }));
    }
  }, [groupsAndTeesSelector]);

  return (
    <TableContainer sx={{ padding: 5 }}>
      <Table sx={{ padding: "0 !important" }}>
        <TableBody>
          <StyledTableRow>
            <StyledTableCell sx={{ minWidth: "200px" }} rowSpan={3}>
              <Box>
                <SideBoxItem value={courseInfo.name} />
                <SideBoxItem value={courseInfo.date} />
                <SideBoxItem value={courseInfo.time} />
              </Box>
            </StyledTableCell>

            {parIndexList.holesList.map(({ label, id, isLabel }) =>
              !isLabel ? (
                <StyledTableCell align="center" key={id}>
                  {label}
                </StyledTableCell>
              ) : (
                <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                  {label}
                </StyledTableCell>
              )
            )}
          </StyledTableRow>

          <StyledTableRow>
            {parIndexList.indexList.map(({ label, id, isLabel }) =>
              !isLabel ? (
                <StyledTableCell align="center" key={id}>
                  {label}
                </StyledTableCell>
              ) : (
                <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                  {label}
                </StyledTableCell>
              )
            )}
          </StyledTableRow>

          <StyledTableRow>
            {parIndexList.parList.map(({ id, label, isLabel }) =>
              !isLabel ? (
                <StyledTableCell align="center" key={id}>
                  {label}
                </StyledTableCell>
              ) : (
                <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                  {label}
                </StyledTableCell>
              )
            )}
          </StyledTableRow>

          {/* 1st opp*/}
          <StyledTableRow>
            <StyledTableCell align="center">
              <OpponentName
                value={
                  groupsAndTeesSelector?.status?.groupsData != null
                    ? groupsAndTeesSelector?.status?.groupsData[0]?.matches
                        ?.matchGroups[0]?.name
                    : "Loading..."
                }
                color="#0000FF"
              />
            </StyledTableCell>
            <StyledTableCell align="center">
              <Box
                className={classes.textBox}
                sx={{
                  backgroundColor:
                    playerInfo.group1.teeColor == "yellow"
                      ? "#FFC700"
                      : playerInfo.group1.teeColor == "blue"
                      ? "#0C2DD8"
                      : playerInfo.group1.teeColor == "black"
                      ? "#000000"
                      : "#C4C4C4",
                }}
              >
                <Box className={classes.numberBox}>
                  {playerInfo.group1.handicapValue}
                </Box>
                <Typography variant="body1" className={classes.textBoxChild}>
                  {playerInfo.group1.teeColor}
                </Typography>
              </Box>
            </StyledTableCell>

            {score != null ? (
              score.group1.map((pl) => {
                return pl.isLabel ? (
                  <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                    {pl.score}
                  </StyledTableCell>
                ) : (
                  <StyledTableCell>
                    <StyledScoreBox pl={pl}>{pl.score || 0}</StyledScoreBox>
                    {pl.benefit && <CustomBadge advantagePoint={1} />}
                  </StyledTableCell>
                );
              })
            ) : (
              <>
                {[...Array(Number(9))].map((_, index) => (
                  <StyledTableCell align="center">0</StyledTableCell>
                ))}

                <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                  0
                </StyledTableCell>

                {[...Array(Number(9))].map((_, index) => (
                  <StyledTableCell align="center">0</StyledTableCell>
                ))}

                <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                  0
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                  0
                </StyledTableCell>
              </>
            )}
          </StyledTableRow>

          {/* vs row  start*/}
          <StyledTableRow>
            <StyledTableCell align="center">
              <OpponentName value="vs" color="#000" />
            </StyledTableCell>
            <StyledTableCell align="center"></StyledTableCell>
            {middleRow.map(({ label, count }, index) => {
              return label == "tie" ? (
                <StyledTableCell sx={{ minWidth: "50px" }} align="center">
                  -
                </StyledTableCell>
              ) : label == "group2" ? (
                <StyledTableCell align="center">
                  <ArrowStatus value={count} color="#FF0000" />
                </StyledTableCell>
              ) : label == "group1" ? (
                <StyledTableCell sx={{ minWidth: "50px" }} align="center">
                  <ArrowStatus value={count} color="#0000FF" />
                </StyledTableCell>
              ) : (
                label == "" && (
                  <StyledTableCell align="center"></StyledTableCell>
                )
              );
            })}

            {[...Array(Number(19 - middleRow?.length))].map(() => (
              <StyledTableCell align="center"></StyledTableCell>
            ))}

            {wonPlayoffInfo != null ? (
              <StyledTableCell align="center" colSpan={2}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color:
                      wonPlayoffInfo.scoreUpGroup == "group1"
                        ? "#0000FF"
                        : "#FF0000",
                  }}
                >
                  Won in playoff
                </Typography>
              </StyledTableCell>
            ) : (
              <>
                <StyledTableCell align="center"></StyledTableCell>
                <StyledTableCell align="center"></StyledTableCell>
              </>
            )}
          </StyledTableRow>

          {/* 2nd opp*/}
          <StyledTableRow>
            <StyledTableCell align="center">
              <OpponentName
                value={
                  groupsAndTeesSelector?.status?.groupsData != null
                    ? groupsAndTeesSelector?.status?.groupsData[0]?.matches
                        ?.matchGroups[1]?.name
                    : "Loading..."
                }
                color="#FF0000"
              />
            </StyledTableCell>
            <StyledTableCell align="center">
              <Box
                className={classes.textBox}
                sx={{
                  backgroundColor:
                    playerInfo.group2.teeColor == "yellow"
                      ? "#FFC700"
                      : playerInfo.group2.teeColor == "blue"
                      ? "#0C2DD8"
                      : playerInfo.group2.teeColor == "black"
                      ? "#000000"
                      : "#C4C4C4",
                }}
              >
                <Box className={classes.numberBox}>
                  {playerInfo.group2.handicapValue}
                </Box>
                <Typography variant="body2" className={classes.textBoxChild}>
                  {playerInfo.group2.teeColor}
                </Typography>
              </Box>
            </StyledTableCell>

            {score != null ? (
              score.group2.map((pl) => {
                return pl.isLabel ? (
                  <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                    {pl.score}
                  </StyledTableCell>
                ) : (
                  <StyledTableCell>
                    <StyledScoreBox pl={pl}>{pl.score || 0}</StyledScoreBox>
                    {pl.benefit && <CustomBadge advantagePoint={1} />}
                  </StyledTableCell>
                );
              })
            ) : (
              <>
                {[...Array(Number(9))].map(() => (
                  <StyledTableCell align="center">0</StyledTableCell>
                ))}

                <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                  0
                </StyledTableCell>

                {[...Array(Number(9))].map(() => (
                  <StyledTableCell align="center">0</StyledTableCell>
                ))}

                <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                  0
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: 700 }} align="center">
                  0
                </StyledTableCell>
              </>
            )}
          </StyledTableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ScoreboardTable;
